import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOAuthIdentityService, OAuthProviderType, useTokenContext, RegisteredButNotLinkedError } from 'nekst-api';
import { useEventsPublisher } from 'event-bus';
import { LOGGED_IN } from 'nekst-api';
import useLoginNavigator from '../loginNavigator';

const TASKS_PAGE_LINK = '/tasks';

export type OAuthCallbackStatus = 'processing' | 'success' | 'error' | 'registered-not-linked';

export interface OAuthCallbackState {
  status: OAuthCallbackStatus;
  errorMessage: string;
  userEmail: string;
}

export function useOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginNavigator = useLoginNavigator();

  // Use refs to prevent unnecessary re-renders and keep services stable
  const oAuthServiceRef = useRef(useOAuthIdentityService());
  const tokenContextRef = useRef(useTokenContext());
  const eventsPublisherRef = useRef(useEventsPublisher());

  // Track processed codes to prevent duplicate processing
  const processedCodesRef = useRef(new Set<string>());

  const [state, setState] = useState<OAuthCallbackState>({
    status: 'processing',
    errorMessage: '',
    userEmail: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Prevent concurrent processing
        if (isProcessing) {
          return;
        }

        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
          setState({
            status: 'error',
            errorMessage: 'OAuth authorization was cancelled or failed.',
            userEmail: ''
          });
          return;
        }

        if (!code) {
          setState({
            status: 'error',
            errorMessage: 'No authorization code received from OAuth provider.',
            userEmail: ''
          });
          return;
        }

        // Check if we've already processed this code
        if (processedCodesRef.current.has(code)) {
          return;
        }

        // Mark this code as being processed
        processedCodesRef.current.add(code);
        setIsProcessing(true);

        // Process the OAuth login
        const token = await oAuthServiceRef.current.login(OAuthProviderType.GOOGLE, { code });

        // Set the token in the context
        await tokenContextRef.current.setToken(token);

        // Publish login event
        setTimeout(() => {
          eventsPublisherRef.current.publish(LOGGED_IN, {});
        }, 500);

        setState({
          status: 'success',
          errorMessage: '',
          userEmail: ''
        });

        // Redirect to main app after a brief success message
        setTimeout(() => {
          navigate(TASKS_PAGE_LINK);
        }, 1500);

      } catch (error) {
        console.error('OAuth callback error:', error);

        if (error instanceof RegisteredButNotLinkedError) {
          setState({
            status: 'registered-not-linked',
            errorMessage: '',
            userEmail: error.email
          });
        } else {
          setState({
            status: 'error',
            errorMessage: 'Failed to complete OAuth login. Please try again.',
            userEmail: ''
          });
        }
      } finally {
        setIsProcessing(false);
      }
    };

    processOAuthCallback();
  }, []);

  const handleRetry = () => {
    loginNavigator.openLoginPage();
  };

  const handleGoToLogin = () => {
    loginNavigator.openLoginPage();
  };

  return {
    ...state,
    handleRetry,
    handleGoToLogin
  };
}
